/**
 * Recipe scaling — along two axes.
 *
 * **Size** ({@link scaleRecipeDoc}) is the physics one: **extensive** quantities (amounts of stuff)
 * scale, **intensive** quantities (ratios, rates, concentrations, settings) do
 * not. `packages/core`'s scaling-invariance property test relies on exactly
 * this split — scale a recipe by `k` and every concentration the engine
 * computes (OG, IBU, SRM) must come back unchanged.
 *
 * | Scales | Does not scale |
 * | --- | --- |
 * | `batchVolumeL` | `boilTimeMinutes`, all step times and temperatures |
 * | fermentable / hop / misc amounts | alpha acid %, extract %, moisture %, attenuation % |
 * | salt grams, acid mL and g | water ion ppm (already per litre), dilution fraction |
 * | mash step `infusionVolumeL` | mash `thicknessLPerKg` (L per kg is intensive) |
 * | | `carbonationVolumes`, `targetMashPh` |
 * | | yeast `packs` |
 * | | the equipment profile |
 *
 * **Counted miscs** (unit `each`) scale, then snap to the nearest half with
 * half as the floor. A tablet is broken in two or not at all, so half a
 * Whirlfloc scaled to a 3 gal batch is still half a tablet, not 0.3 of one.
 * Undo is unaffected: `recipe.scale` inverts by restoring the prior document.
 *
 * **Yeast packs** are excluded deliberately: doubling a batch does not
 * double a pitch by simple proportion (viability, cell counts and starter
 * growth all enter), and quietly writing `2.4 packs` would be a worse answer
 * than leaving the number alone and letting the pitch-rate calculator speak
 * (PLAN §5.6).
 *
 * **The equipment profile** is excluded because it describes the brewer's
 * system, not the recipe: scaling a recipe 2× does not buy a bigger kettle.
 * Scaling a recipe onto a genuinely bigger system is a `recipe.setEquipment`
 * followed by a scale, and a *test* of scaling invariance against core must
 * scale the profile's fixed losses itself.
 *
 * **Efficiency** ({@link rescaleEfficiencyDoc}) is the other axis, and it is
 * not a scaling at all in the physics sense — it moves *some* of the grist and
 * leaves the rest exactly where it was. See that function's own note.
 */

import { fermentableEfficiencyFraction } from '@beerwright/core';
import type { RecipeFermentable, RecipeMisc } from './ingredients';
import type { RecipeDoc } from './recipe';

/**
 * A copy of `doc` scaled by `factor`.
 *
 * Pure and allocation-shy: lists whose contents are unchanged (yeasts, the
 * fermentation schedule) are shared by reference.
 *
 * @param doc - The recipe to scale.
 * @param factor - Multiplier, > 0. `1` returns a structurally equal document.
 */
export function scaleRecipeDoc(doc: RecipeDoc, factor: number): RecipeDoc {
  return {
    ...doc,
    batchVolumeL: doc.batchVolumeL * factor,
    fermentables: doc.fermentables.map((f) => ({ ...f, amountKg: f.amountKg * factor })),
    hops: doc.hops.map((h) => ({ ...h, amountKg: h.amountKg * factor })),
    miscs: doc.miscs.map((m) => ({ ...m, amount: scaleMiscAmount(m, factor) })),
    mashSteps: doc.mashSteps.map((s) => ({
      ...s,
      infusionVolumeL: s.infusionVolumeL === null ? null : s.infusionVolumeL * factor,
    })),
    water:
      doc.water === null
        ? null
        : {
            ...doc.water,
            saltAdditions: doc.water.saltAdditions.map((s) => ({
              ...s,
              grams: s.grams * factor,
            })),
            acidAdditions: doc.water.acidAdditions.map((a) => ({
              ...a,
              millilitres: a.millilitres === null ? null : a.millilitres * factor,
              grams: a.grams === null ? null : a.grams * factor,
            })),
          },
  };
}

/**
 * A misc amount scaled by `factor`; counted units snap to halves (see the header).
 *
 * Exported for applying an additions profile, which scales its rows onto the
 * recipe's batch size by this same rule.
 */
export function scaleMiscAmount(misc: RecipeMisc, factor: number): number {
  const scaled = misc.amount * factor;
  if (misc.unit !== 'each') return scaled;
  // A row the brewer left at zero stays at zero rather than becoming half.
  return misc.amount === 0 ? 0 : Math.max(0.5, Math.round(scaled * 2) / 2);
}

// ---------------------------------------------------------------------------
// Efficiency rescaling
// ---------------------------------------------------------------------------

/** Where a recipe's grist was written for, and where it is going. */
export interface EfficiencyRescale {
  /** The brewhouse efficiency the recipe's amounts were computed at, **%**. */
  readonly fromPercent: number;
  /** The brewhouse efficiency the recipe is being moved onto, **%**. */
  readonly toPercent: number;
}

/**
 * Whether the equipment profile's efficiency is the one this addition converts
 * at — the single question efficiency rescaling turns on.
 *
 * **Asked of core, never answered here.** The classification lives in
 * `fermentableEfficiencyFraction`, and this probes it at two different profile
 * efficiencies to see whether the answer moves. Grain and steeping grain with
 * `use: 'mash'` come back 1.0 and 0.5 and are therefore profile-dependent;
 * extract, sugar and non-fermentables come back 1.0 both times (they dissolve
 * completely), steeped or boiled grain comes back at the fixed 50 % steep
 * figure both times, and grain in the whirlpool or the fermenter comes back 0
 * both times. Copying that switch into this file would be a second copy of the
 * rule, and the two would drift the first time a fermentable class is added;
 * the probe cannot. It is also the idiom core itself uses to invert the same
 * classification in `mashEfficiencyFromMeasurement`.
 *
 * This leans on core's function taking a bare `{ class, use }` and keeping the
 * switch on its side. If either changes, this probe has to change with it.
 */
export function efficiencyFollowsTheProfile(fermentable: {
  readonly class: RecipeFermentable['class'];
  readonly use: RecipeFermentable['use'];
}): boolean {
  return (
    fermentableEfficiencyFraction(fermentable, 100) !==
    fermentableEfficiencyFraction(fermentable, 50)
  );
}

/**
 * A copy of `doc` whose grist has been moved from one brewhouse efficiency to
 * another — the "this recipe was written for somebody else's rig" transform.
 *
 * **The invariant**: the rescaled recipe predicts the same extract on
 * `toPercent` as the original did on `fromPercent`. Extract is bookkept in
 * point·litres (`kg × pointsPerKgPerL × efficiency`), which is linear in both
 * mass and efficiency, so multiplying the mass by `fromPercent / toPercent`
 * cancels the efficiency change exactly:
 *
 * ```
 * kg × (from / to) × ppg × to  ≡  kg × ppg × from
 * ```
 *
 * A rig that runs at 68 % needs `75 / 68` of the grain a 75 % recipe printed,
 * and gets the gravity the recipe's author meant.
 *
 * **Only part of the grist moves.** The factor is applied exclusively to
 * additions whose efficiency depends on the profile — see
 * {@link efficiencyFollowsTheProfile}, which asks core rather than restating
 * the rule. Extract, sugar and non-fermentables convert at 1.0 whatever the rig
 * does, and steeped or boiled grain converts at a fixed 50 %: their extract does
 * not move when the profile's efficiency moves, so scaling them would *break*
 * the invariant rather than preserve it. Add 20 % more dextrose to a saison
 * because your mash runs cold and you have simply made a stronger, drier beer.
 * This selectivity is the whole feature; the arithmetic is one multiplication.
 *
 * **The residuals, stated rather than hidden.** More grain absorbs more water
 * and displaces more volume, so the volume waterfall shifts a hair and the
 * *landed* OG is not exactly the OG the recipe printed — a point or two,
 * usually on the strong side. Nothing here iterates to close that gap: the
 * number a brewer sees is whatever the engine computes from the transformed
 * document, and the scale dialog shows OG before → after so the residual is
 * visible instead of smoothed away. The second residual is the efficiency
 * *kinds*. Mash and brewhouse efficiency are different figures; this transform
 * is a ratio, so it is right exactly when `fromPercent` and `toPercent` are the
 * same kind of number. The dialog labels both fields "brewhouse efficiency" for
 * that reason, and an imported file's `EFFICIENCY` — a brewhouse figure, see
 * `interchange`'s `equipmentFromSource` — is what prefills the source.
 *
 * Pure and allocation-shy, like {@link scaleRecipeDoc}: everything but the
 * fermentable list is shared by reference, and an addition that does not move
 * is shared individually.
 *
 * @param doc - The recipe to rescale.
 * @param rescale - Source and target efficiencies, **%**. Both must be above 0
 *   and at most 100; the bound is enforced at the command boundary by
 *   `recipeScalePayloadSchema`, exactly as {@link scaleRecipeDoc}'s `factor` is.
 *   Equal figures return a structurally equal document.
 */
export function rescaleEfficiencyDoc(doc: RecipeDoc, rescale: EfficiencyRescale): RecipeDoc {
  const factor = rescale.fromPercent / rescale.toPercent;
  if (factor === 1) return { ...doc };
  return {
    ...doc,
    fermentables: doc.fermentables.map((f) =>
      efficiencyFollowsTheProfile(f) ? { ...f, amountKg: f.amountKg * factor } : f,
    ),
  };
}
